import { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL, WS_BASE_URL } from '../utils/apiConfig';

/**
 * Custom hook to get live cryptocurrency prices
 *
 * HYBRID ARCHITECTURE:
 * - Primary: WebSocket subscription to price updates (real-time from exchanges)
 * - Fallback: REST API polling (if WebSocket fails or prices not yet available)
 *
 * Prices come from priceTickerService which aggregates from:
 * - Binance Futures (!ticker@arr)
 * - OKX (tickers channel)
 * - Bybit (tickers.{symbol})
 * - Hyperliquid (allMids)
 */
export function useLivePrices() {
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);

  // Store last successful prices for fallback
  const lastValidPricesRef = useRef({});
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const fallbackIntervalRef = useRef(null);
  const retryIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  const hasPricesRef = useRef(false);
  const skeletonTimerRef = useRef(null);

  // Batching: accumulate price updates and flush once per animation frame
  const pendingPricesRef = useRef({});
  const rafRef = useRef(null);

  // Schedule a batched state update: accumulates changes in pendingPricesRef
  // and flushes them in a single setPrices call on the next animation frame.
  const schedulePriceFlush = useCallback(() => {
    if (rafRef.current) return; // already scheduled
    rafRef.current = requestAnimationFrame(() => {
      const pending = pendingPricesRef.current;
      if (Object.keys(pending).length > 0) {
        setPrices(prev => {
          const updated = { ...prev, ...pending };
          lastValidPricesRef.current = updated;
          return updated;
        });
        pendingPricesRef.current = {};
      }
      rafRef.current = null;
    });
  }, []);

  // Fallback: fetch from REST API
  const fetchPricesFromApi = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/prices`);
      if (!response.ok) throw new Error('API error');

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Failed');

      // Handle new format (object) vs legacy format (array)
      let pricesObj = {};
      if (Array.isArray(result.prices)) {
        // Legacy format
        result.prices.forEach(item => {
          const coin = item.symbol.replace('USDT', '');
          pricesObj[coin] = parseFloat(item.price);
        });
      } else if (typeof result.prices === 'object') {
        // New format from ticker service
        Object.entries(result.prices).forEach(([symbol, data]) => {
          // data can be just a number or an object with price property
          const price = typeof data === 'number' ? data : data.price;
          pricesObj[symbol] = price;
        });
      }

      if (Object.keys(pricesObj).length > 0) {
        hasPricesRef.current = true;
        Object.assign(pendingPricesRef.current, pricesObj);
        schedulePriceFlush();
        setLoading(false);

        // Got prices — stop retry polling
        if (retryIntervalRef.current) {
          clearInterval(retryIntervalRef.current);
          retryIntervalRef.current = null;
        }
      }
    } catch (error) {
      // Use cached prices
      if (Object.keys(lastValidPricesRef.current).length > 0) {
        setPrices(lastValidPricesRef.current);
      }
      setLoading(false);
    }
  }, [schedulePriceFlush]);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Don't reconnect if already connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const isDev = window.location.port === '5173' || window.location.port === '5174' || window.location.port === '5175';
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = isDev ? (WS_BASE_URL || `ws://localhost:${window.location.port}/ws`) : (WS_BASE_URL || `${protocol}//${window.location.host}`);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setConnected(true);

        // Stop REST fallback polling only if we already have prices.
        // If we don't have prices yet, keep polling — WS being connected
        // doesn't guarantee price broadcasts are flowing.
        if (hasPricesRef.current && fallbackIntervalRef.current) {
          clearInterval(fallbackIntervalRef.current);
          fallbackIntervalRef.current = null;
        }

        // Subscribe to price updates
        ws.send(JSON.stringify({
          type: 'subscribe',
          filters: {
            whaleTradesMinVolume: 1000000000, // High threshold = no trades, just prices
            liquidationsMinVolume: 1000000000,
            tradeTypes: [] // No trades
          }
        }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Handle price updates from priceTickerService
          if (message.type === 'prices' && message.data) {
            hasPricesRef.current = true;
            Object.assign(pendingPricesRef.current, message.data);
            schedulePriceFlush();
            setLoading(false);

            // WS is delivering prices — stop all REST polling
            if (fallbackIntervalRef.current) {
              clearInterval(fallbackIntervalRef.current);
              fallbackIntervalRef.current = null;
            }
            if (retryIntervalRef.current) {
              clearInterval(retryIntervalRef.current);
              retryIntervalRef.current = null;
            }
          }
        } catch (e) {
          // Ignore parse errors
        }
      };

      ws.onclose = (event) => {
        setConnected(false);
        wsRef.current = null;

        // Don't create new intervals/timeouts if unmounted
        if (!mountedRef.current) return;

        // Start REST fallback
        if (!fallbackIntervalRef.current) {
          fetchPricesFromApi(); // Immediate fetch
          fallbackIntervalRef.current = setInterval(fetchPricesFromApi, 5000);
        }

        // Schedule reconnect
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
      };

      ws.onerror = (error) => {
        // onclose will handle reconnection
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('[useLivePrices] Failed to connect WebSocket:', error);

      // Don't create new intervals/timeouts if unmounted
      if (!mountedRef.current) return;

      // Start REST fallback
      if (!fallbackIntervalRef.current) {
        fetchPricesFromApi();
        fallbackIntervalRef.current = setInterval(fetchPricesFromApi, 5000);
      }
      // Try reconnect
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
    }
  }, [fetchPricesFromApi, schedulePriceFlush]);

  useEffect(() => {
    // Initial fetch from API (instant data while WebSocket connects)
    fetchPricesFromApi();

    // Connect WebSocket
    connectWebSocket();

    // Retry polling: if initial fetch returned empty prices,
    // keep retrying every 2s until we get data (from API or WS)
    retryIntervalRef.current = setInterval(() => {
      if (hasPricesRef.current) {
        clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
        return;
      }
      fetchPricesFromApi();
    }, 2000);

    // Skeleton timeout: after 8s with no prices, exit skeleton state
    // so the UI shows coin pills with "..." instead of pulsing bones
    skeletonTimerRef.current = setTimeout(() => {
      if (!hasPricesRef.current) {
        setLoading(false);
      }
    }, 8000);

    // Cleanup
    return () => {
      mountedRef.current = false; // Prevent onclose from creating zombie intervals
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (fallbackIntervalRef.current) {
        clearInterval(fallbackIntervalRef.current);
      }
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
      }
      if (skeletonTimerRef.current) {
        clearTimeout(skeletonTimerRef.current);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [connectWebSocket, fetchPricesFromApi]);

  // Format price for display
  const formatPrice = useCallback((price) => {
    if (!price || price === 0) return '$0.00';

    if (price < 0.0001) {
      return `$${price.toFixed(8)}`;
    } else if (price < 0.01) {
      return `$${price.toFixed(6)}`;
    } else if (price < 1) {
      return `$${price.toFixed(4)}`;
    } else if (price < 100) {
      return `$${price.toFixed(2)}`;
    } else {
      return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  }, []);

  // Get price for a specific symbol
  const getPrice = useCallback((symbol) => {
    const normalized = symbol?.toUpperCase().replace(/USDT$|USD$/i, '');
    return prices[normalized] || null;
  }, [prices]);

  return {
    prices,
    loading,
    connected,
    formatPrice,
    getPrice
  };
}
